import * as React from "react"
import { useQuery } from "react-query"

import { useOrgs } from "../../../components/providers/OrgProvider"
import {
  Bbox,
  getMultiClusterBounds,
  getMultiZoneBounds,
  getBboxQueryParams,
  BBOX_PADDING,
  createBboxFromBounds,
} from "../../helpers/map"
import { WORLD_BBOX } from "../../config"
import { useGetServiceZonesQuery } from "./useGetMapZones"
import { ClusterEntity } from "../../types/cluster"
import { useMapBBox } from "../../../components/providers/MapBBoxProvider"

export interface ClustersResponse {
  clusters: ClusterEntity[]
}

export const useGetInitialBikesMapBbox = (): Bbox | undefined => {
  const { currentOrgs } = useOrgs()
  const { serviceZones, serviceLoading } = useGetServiceZonesQuery()
  const { currentBBox } = useMapBBox()

  const serviceBounds = React.useMemo(
    () => serviceZones && serviceZones.length > 0 && getMultiZoneBounds(serviceZones),
    [serviceZones],
  )

  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  // If there are no services, we get all clusters and attempt to set bbox based of all of them
  const { data: clusterData, isLoading: isClusterLoading } = useQuery<ClustersResponse, string>(
    `clusters/?organizations=${currentOrgsUuids.join()}${getBboxQueryParams(WORLD_BBOX)}`,
    {
      staleTime: Infinity,
      enabled: serviceLoading ? false : serviceZones && serviceZones.length > 0 ? false : true,
    },
  )

  const clusters = clusterData?.clusters

  const initialBBox = React.useMemo(() => {
    if (serviceLoading || isClusterLoading) {
      return
    }

    // if a current BBox is already set, use it
    if (currentBBox) {
      return currentBBox
    }

    // If service zones, set bounds to bbox of all service zones
    if (serviceBounds) {
      return createBboxFromBounds(serviceBounds)
    }

    if (!clusters || clusters.length === 0) {
      // No clusters, set to world bbox
      return WORLD_BBOX
    }

    // If single cluster and cluster bbox is a set of coords
    if (clusters?.length === 1 && clusters[0].bbox.type === "Point") {
      const singleClusterCoords = clusters[0].centroid.coordinates
      const singleClusterBbox: Bbox = [
        [singleClusterCoords[0] - BBOX_PADDING, singleClusterCoords[1] - BBOX_PADDING],
        [singleClusterCoords[0] + BBOX_PADDING, singleClusterCoords[1] + BBOX_PADDING],
      ]

      return singleClusterBbox
    }

    //  Filter bbox clusters and set bounds to bbox of all clusters
    const clusterBounds = getMultiClusterBounds(clusters)
    if (!clusterBounds || clusters.length === 0) {
      // If something went wrong when calculating multipolygon bbox, set to world bbox
      return WORLD_BBOX
    }

    const multiClusterBbox: Bbox = [
      [clusterBounds[0].lng - BBOX_PADDING, clusterBounds[0].lat - BBOX_PADDING],
      [clusterBounds[1].lng + BBOX_PADDING, clusterBounds[1].lat + BBOX_PADDING],
    ]
    return multiClusterBbox

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceBounds, serviceLoading, isClusterLoading, clusters])

  return initialBBox
}
